'use client';
import { useCallback, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';

import {
  useAuth,
  useClobCredentials,
  useClobOrder,
  useCollateralOnramp,
  useSignInHandlers,
  useT,
} from '@/hooks';
import { useMarketPrices, useOrderBook, useTickSize } from '@/services';
import { usePortfolioSummary, usePositions } from '@/services/query';
import { openModal, openPositionActionModal, useAppDispatch } from '@/store';
import { setSetupRequested } from '@/store/slices/auth.slice';
import type { EventMarket, EventOutcome, EventResponse, Market, Outcome } from '@/types';
import {
  getMarketTokenIds,
  getOutcomes,
  getPreferredOutcomeDisplayPrice,
  getSpreadLine,
  isMarketResolved,
  normalizeMarketQuestion,
  resolveOutcomeData,
} from '@/utils';
import { ResolvedTradeView } from '../TradeModal';
import {
  AmountInput,
  ExpirationSelector,
  LimitPriceInput,
  SharesInput,
  SideSelector,
  TradeActionButton,
  TradeHeader,
  TradeSummary,
  TradeTabs,
} from './trading';

import { calculateExpirationTimestamp } from '@/constants/market.constants';

interface TradingSidebarProps {
  event?: EventResponse;
  market: Market | EventMarket;
  selectedOutcome: Outcome | EventOutcome | null;
  selectedSide?: 'yes' | 'no';
  onSideChange?: (side: 'yes' | 'no') => void;
  activeTab?: 'buy' | 'sell';
  onTabChange?: (tab: 'buy' | 'sell') => void;
  yesLabel?: string;
  noLabel?: string;
  yesBtnColor?: string;
  noBtnColor?: string;
}

export function TradingSidebar({
  event,
  market,
  selectedOutcome,
  selectedSide = 'yes',
  onSideChange,
  activeTab: propsActiveTab,
  onTabChange,
  yesLabel,
  noLabel,
  yesBtnColor,
  noBtnColor,
}: TradingSidebarProps) {
  const { onLogin } = useSignInHandlers((error) => {
    if (error) console.error('Sign in error:', error);
  });
  // Map "buy outcome=yes" to "buy" tab, "buy outcome=no" is effectively selling "yes" in some models,
  // but here we stick to the requested UI where Buy/Sell tabs are separate from Yes/No buttons.
  // However, the user request implies clicking "Buy Yes" on a card should select "Yes" here.
  // We'll keep the Buy/Sell tab local for now as it wasn't explicitly asked to be lifted,
  // but we MUST sync the "Yes/No" selection.

  const activeTab = propsActiveTab || 'buy';

  function setActiveTab(tab: 'buy' | 'sell') {
    if (onTabChange) {
      onTabChange(tab);
    }
  }

  const [activeSide, setActiveSide] = useState<'yes' | 'no'>(selectedSide || 'yes');
  // Internal state fallback not needed for selectedSide if we default to 'yes' from props,
  // effectively making it controlled-focused.

  const title = normalizeMarketQuestion(market);

  const [orderType, setOrderType] = useState<'market' | 'limit' | 'more'>('market');
  const [limitPrice, setLimitPrice] = useState('0');
  const [shares, setShares] = useState('0');
  const [amount, setAmount] = useState('0');
  const [setExpiration, setSetExpiration] = useState(false);
  const [expirationValue, setExpirationValue] = useState('1m');

  const mounted = useSyncExternalStore(
    useCallback(() => () => {}, []),
    () => true,
    () => false
  );

  const { t } = useT();

  const { isAuthenticated, user, isReadyToTrade } = useAuth();
  const { data: portfolioSummary } = usePortfolioSummary();
  const { wrap, isPending: isUpgradePending } = useCollateralOnramp();
  const { provisionCredentials, resetCredentials } = useClobCredentials();
  const lastAuthRecoveryAtRef = useRef(0);

  // Cast because the query return type in portfolio.queries.ts adds requiresMigration dynamically
  const requiresMigration = (portfolioSummary as any)?.requiresMigration ?? false;
  const legacyUsdcBalance = (portfolioSummary as any)?.legacyUsdcBalance ?? 0;

  const cashBalance = portfolioSummary?.cashBalance || 0;
  const { data: positions, isFetching: isPositionsFetching } = usePositions(
    user?.proxyWalletAddress,
    event?.id
  );
  const dispatch = useAppDispatch();

  const { price, priceNo, outcomeNameYes, outcomeNameNo } = useMemo(() => {
    return resolveOutcomeData(market as any);
  }, [market]);

  const marketOutcomes = useMemo(() => getOutcomes(market as any), [market]);
  const isClosedMarket =
    (market as any)?.closed === true ||
    (market as any)?.closed === 'true' ||
    (market as any)?.status === 'closed' ||
    (market as any)?.status === 'resolved';

  const isFlipped = useMemo(() => {
    if (event?.gameId && (event.homeTeam || event.awayTeam) && marketOutcomes.length >= 2) {
      const awayName = event.awayTeam?.name?.toLowerCase().trim() || '';
      const awayAbbr = event.awayTeam?.abbreviation?.toLowerCase().trim() || '';
      const firstOutcomeName = (marketOutcomes[0]?.name || '').toLowerCase().trim();

      // Ensure HOME is always on top to match listing and standard notation.
      // If Outcome 0 matches the AWAY team, we must flip.
      const firstIsAway =
        (awayName !== '' &&
          (firstOutcomeName === awayName || firstOutcomeName.includes(awayName))) ||
        (awayAbbr !== '' && (firstOutcomeName === awayAbbr || firstOutcomeName.includes(awayAbbr)));

      return firstIsAway;
    }
    return false;
  }, [marketOutcomes, event]);

  const yesOutcome = useMemo(() => {
    return marketOutcomes.find((o) => (o?.name || '').toLowerCase() === 'yes') || marketOutcomes[0];
  }, [marketOutcomes]);

  const noOutcome = useMemo(() => {
    return marketOutcomes.find((o) => (o?.name || '').toLowerCase() === 'no') || marketOutcomes[1];
  }, [marketOutcomes]);

  const getOutcomeColor = (name: string, isYes: boolean) => {
    const lowerName = name.toLowerCase();
    const marketTitle = (market as any)?.question || (market as any)?.title || '';
    const type = (market as any)?.sportsMarketType || (market as any)?.sportsType || '';
    const isSport = !!(event?.gameId || (event?.homeTeam && event?.awayTeam));
    const isTotal =
      isSport &&
      (type.toLowerCase().includes('total') || marketTitle.toLowerCase().includes('o/u'));

    if (isTotal) {
      if (lowerName === 'over' || lowerName === 'yes' || isYes) return '#359a5e';
      return '#cb3131';
    }

    if (lowerName === 'over' || lowerName === 'yes') return '#22c55e';
    if (lowerName === 'under' || lowerName === 'no') return '#ef4444';

    if (isSport && event && (event.homeTeam || event.awayTeam)) {
      const homeNameRaw = event.homeTeam?.name?.toLowerCase().trim() || '';
      const awayNameRaw = event.awayTeam?.name?.toLowerCase().trim() || '';
      const homeAbbrRaw = event.homeTeam?.abbreviation?.toLowerCase().trim() || '';
      const awayAbbrRaw = event.awayTeam?.abbreviation?.toLowerCase().trim() || '';

      if (
        (homeNameRaw && (lowerName.includes(homeNameRaw) || homeNameRaw.includes(lowerName))) ||
        (homeAbbrRaw && lowerName.includes(homeAbbrRaw))
      ) {
        return event.homeTeam?.color;
      }
      if (
        (awayNameRaw && (lowerName.includes(awayNameRaw) || awayNameRaw.includes(lowerName))) ||
        (awayAbbrRaw && lowerName.includes(awayAbbrRaw))
      ) {
        return event.awayTeam?.color;
      }

      const teamColor = isYes ? event.homeTeam?.color : event.awayTeam?.color;
      if (teamColor) return teamColor;
    }
    return isYes ? '#22c55e' : '#ef4444';
  };

  const yesBtnColorDerived = getOutcomeColor(outcomeNameYes, true);
  const noBtnColorDerived = getOutcomeColor(outcomeNameNo, false);

  const yesBtnColorFinal = yesBtnColor || yesBtnColorDerived;
  const noBtnColorFinal = noBtnColor || noBtnColorDerived;

  const { yesLabelFinal, noLabelFinal } = useMemo(() => {
    let yes = yesLabel || outcomeNameYes;
    let no = noLabel || outcomeNameNo;

    const isSport = !!(event?.gameId || (event?.homeTeam && event?.awayTeam));

    if (isSport && event && (event.homeTeam || event.awayTeam)) {
      const homeName = event.homeTeam?.name?.toLowerCase().trim() || '';
      const awayName = event.awayTeam?.name?.toLowerCase().trim() || '';
      const homeAbbr = event.homeTeam?.abbreviation || '';
      const awayAbbr = event.awayTeam?.abbreviation || '';
      const type = (market as any)?.sportsMarketType || (market as any)?.sportsType || '';
      const lineStr = (market as any)?.line || (market as any)?.groupItemThreshold || '';
      const lineVal = parseFloat(String(lineStr));

      const processLabel = (label: string, isAwayRow: boolean) => {
        const lowerLabel = label.toLowerCase().trim();
        let processed = label;

        const isTotal =
          type.toLowerCase().includes('total') ||
          (market as any)?.question?.toLowerCase().includes('o/u');
        const isSpread =
          type.toLowerCase().includes('spread') ||
          type.toLowerCase().includes('handicap') ||
          type === 'puck line';

        if (isTotal) {
          if (lowerLabel === 'yes' || lowerLabel === 'over') return `OVER ${lineStr}`.toUpperCase();
          if (lowerLabel === 'no' || lowerLabel === 'under')
            return `UNDER ${lineStr}`.toUpperCase();
        }

        if (lowerLabel === 'yes' || lowerLabel === 'no') return label.toUpperCase();

        const isHome = homeName && (lowerLabel.includes(homeName) || homeName.includes(lowerLabel));
        const isAwayTeam =
          awayName && (lowerLabel.includes(awayName) || awayName.includes(lowerLabel));

        if (isHome) {
          processed = homeAbbr || homeName || label;
        } else if (isAwayTeam) {
          processed = awayAbbr || awayName || label;
        }

        if (isSpread) {
          const isAway =
            (awayName && (lowerLabel.includes(awayName) || awayName.includes(lowerLabel))) ||
            (awayAbbr && lowerLabel.includes(awayAbbr));
          const spreadLine = getSpreadLine(
            market as any,
            isAway ? event?.awayTeam?.name : event?.homeTeam?.name,
            isAway
          );
          if (spreadLine) {
            processed = `${processed} ${spreadLine}`;
          }
        } else if (isTotal && lineStr && !processed.includes(String(lineStr))) {
          processed = `${processed} ${lineStr}`;
        }

        return processed.toUpperCase();
      };

      yes = processLabel(yes, isFlipped);
      no = processLabel(no, !isFlipped);
    }

    return { yesLabelFinal: yes, noLabelFinal: no };
  }, [yesLabel, noLabel, outcomeNameYes, outcomeNameNo, event, market, isFlipped]);

  const tokenIds = useMemo(() => {
    return getMarketTokenIds(market as any);
  }, [market.clobTokenIds]);

  const { data: realtimePrices } = useMarketPrices(tokenIds, true);
  const effectivePrices = realtimePrices;

  const yesPrice = useMemo(() => {
    const isSellTab = activeTab === 'sell';
    const fallbackPrice = isSellTab
      ? (yesOutcome?.bestBid ?? price)
      : (yesOutcome?.bestAsk ?? price);
    const baseYesPrice = getPreferredOutcomeDisplayPrice(
      isSellTab ? yesOutcome?.bestBid : yesOutcome?.bestAsk,
      fallbackPrice,
      { isClosed: isClosedMarket }
    );
    if (!effectivePrices?.[tokenIds[0]]) return baseYesPrice ?? price;
    return isSellTab
      ? effectivePrices[tokenIds[0]].bidPrice
      : effectivePrices[tokenIds[0]].askPrice;
  }, [activeTab, effectivePrices, isClosedMarket, price, tokenIds, yesOutcome]);

  const noPrice = useMemo(() => {
    const isSellTab = activeTab === 'sell';
    const fallbackPrice = isSellTab
      ? (noOutcome?.bestBid ?? priceNo)
      : (noOutcome?.bestAsk ?? priceNo);
    const baseNoPrice = getPreferredOutcomeDisplayPrice(
      isSellTab ? noOutcome?.bestBid : noOutcome?.bestAsk,
      fallbackPrice,
      { isClosed: isClosedMarket }
    );
    const fallbackVal =
      baseNoPrice ?? (Number.isFinite(yesPrice) ? Math.max(0, 1 - yesPrice) : priceNo);
    if (!effectivePrices?.[tokenIds[1]]) return fallbackVal;
    return isSellTab
      ? effectivePrices[tokenIds[1]].bidPrice
      : effectivePrices[tokenIds[1]].askPrice;
  }, [activeTab, effectivePrices, isClosedMarket, noOutcome, priceNo, tokenIds, yesPrice]);

  const limitPriceNum = parseFloat(limitPrice.replace(/[^0-9.]/g, '')) || 0;
  const sharesNum = parseFloat(shares.replace(/[^0-9.]/g, '')) || 0;

  const selectedTokenId = useMemo(() => {
    const targetOutcome = selectedSide === 'yes' ? yesOutcome : noOutcome;

    if (targetOutcome && market) {
      const outcomes = getOutcomes(market as any);
      const idx = outcomes.findIndex((o) => o.id === targetOutcome.id);
      if (idx !== -1 && tokenIds[idx]) {
        return tokenIds[idx];
      }
    }

    // Fallback to indexing by selectedSide ('yes'=0, 'no'=1)
    return selectedSide === 'yes' ? tokenIds[0] : tokenIds[1];
  }, [selectedSide, tokenIds, market, yesOutcome, noOutcome]);

  const selectedOutcomeIndex = useMemo(() => {
    if (!selectedOutcome || !market) return -1;
    const outcomes = getOutcomes(market as any);
    return outcomes.findIndex((o) => o.id === selectedOutcome.id);
  }, [selectedOutcome, market]);

  const { data: tickSizeData } = useTickSize(selectedTokenId || '', true);
  const { data: orderBook } = useOrderBook(
    selectedTokenId || '',
    Boolean(selectedTokenId) && orderType === 'market'
  );
  const parsedTickSize = parseFloat(tickSizeData as any);
  const tickSize = !isNaN(parsedTickSize) && parsedTickSize > 0 ? parsedTickSize : 0.01;

  const userHolding = useMemo(() => {
    if (!positions || !selectedTokenId) return 0;
    const pos = positions.find((p: any) => p.asset === selectedTokenId);
    return pos ? parseFloat(pos.size) : 0;
  }, [positions, selectedTokenId]);

  // Derived values for summary
  const currentPrice = selectedSide === 'yes' ? yesPrice : noPrice;
  const effectivePrice = orderType === 'limit' ? limitPriceNum / 100 : currentPrice;
  const amountNum = parseFloat(amount.replace(/[^0-9.]/g, '')) || 0;

  const averageFillPrice = useMemo(() => {
    if (!orderBook || orderType !== 'market') return undefined;

    if (activeTab === 'buy' && amountNum > 0) {
      const asks = [...(orderBook.asks || [])].sort((a, b) => Number(a.price) - Number(b.price));
      let remainingAmount = amountNum;
      let totalShares = 0;
      let totalSpent = 0;

      for (const level of asks) {
        const levelPrice = Number(level.price);
        const levelSize = Number(level.size);
        if (!levelPrice || !levelSize || levelPrice <= 0 || levelSize <= 0) continue;

        const maxCostAtLevel = levelPrice * levelSize;
        if (remainingAmount >= maxCostAtLevel) {
          totalSpent += maxCostAtLevel;
          totalShares += levelSize;
          remainingAmount -= maxCostAtLevel;
        } else {
          const partialShares = remainingAmount / levelPrice;
          totalSpent += remainingAmount;
          totalShares += partialShares;
          remainingAmount = 0;
          break;
        }
      }

      return totalShares > 0 ? totalSpent / totalShares : undefined;
    }

    if (activeTab === 'sell' && sharesNum > 0) {
      const bids = [...(orderBook.bids || [])].sort((a, b) => Number(b.price) - Number(a.price));
      let remainingShares = sharesNum;
      let soldShares = 0;
      let received = 0;

      for (const level of bids) {
        const levelPrice = Number(level.price);
        const levelSize = Number(level.size);
        if (!levelPrice || !levelSize || levelPrice <= 0 || levelSize <= 0) continue;

        const filledShares = Math.min(remainingShares, levelSize);
        soldShares += filledShares;
        received += filledShares * levelPrice;
        remainingShares -= filledShares;
        if (remainingShares <= 0) break;
      }

      return soldShares > 0 ? received / soldShares : undefined;
    }

    return undefined;
  }, [orderBook, orderType, activeTab, amountNum, sharesNum]);

  // Calculate shares and cost based on mode
  let calculatedShares = 0;
  let totalCost = 0;

  if (activeTab === 'buy') {
    if (orderType === 'market') {
      totalCost = amountNum;
      const execPrice = averageFillPrice || effectivePrice;
      calculatedShares = execPrice > 0 ? amountNum / execPrice : 0;
    } else {
      totalCost = sharesNum * effectivePrice;
      calculatedShares = sharesNum;
    }
  } else {
    // Sell
    totalCost = sharesNum * effectivePrice; // These are proceeds
    calculatedShares = sharesNum;
  }

  const potReturn = activeTab === 'buy' ? calculatedShares : totalCost;
  const potProfit = activeTab === 'buy' ? Math.max(0, calculatedShares - totalCost) : 0;

  const { submitOrder, isSubmitting, error: orderError, orderId } = useClobOrder();

  // Handlers
  function handleBuyTabClick() {
    setActiveTab('buy');
  }

  function handleSellTabClick() {
    setActiveTab('sell');
  }

  function handleYesClick() {
    if (!isAuthenticated) {
      onLogin();
      return;
    }
    if (!user?.isReferredUser) {
      dispatch(openModal('referral'));
      return;
    }
    if (onSideChange) {
      onSideChange('yes');
    }
  }

  function handleNoClick() {
    if (!isAuthenticated) {
      onLogin();
      return;
    }
    if (!user?.isReferredUser) {
      dispatch(openModal('referral'));
      return;
    }
    if (onSideChange) {
      onSideChange('no');
    }
  }

  function handleLimitPriceChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 100) {
      setLimitPrice('100');
    } else {
      setLimitPrice(value);
    }
  }

  function handleLimitPriceDecrease() {
    const current = parseFloat(limitPrice.replace(/[^0-9.]/g, '')) || 0;
    // Tick size is in tokens (e.g. 0.01), UI uses cents (e.g. 52.1)
    const step = tickSize * 100;
    const nextVal = Math.max(0, current - step);
    setLimitPrice(!isNaN(nextVal) ? nextVal.toFixed(step < 1 ? 1 : 0) : '0');
  }

  function handleLimitPriceIncrease() {
    const current = parseFloat(limitPrice.replace(/[^0-9.]/g, '')) || 0;
    const step = tickSize * 100;
    const nextVal = Math.min(100, current + step);
    setLimitPrice(!isNaN(nextVal) ? nextVal.toFixed(step < 1 ? 1 : 0) : '0');
  }

  function handleSharesChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(value);
    if (numValue > 10000000) {
      setShares('10000000');
    } else {
      setShares(value);
    }
  }

  function handleShareQuickAmount(amount: number) {
    const current = parseFloat(shares.replace(/[^0-9.]/g, '')) || 0;
    const nextVal = Math.min(10000000, Math.max(0, current + amount));
    setShares(nextVal.toString());
  }

  function handleAmountChange(e: React.ChangeEvent<HTMLInputElement>) {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    const numValue = parseFloat(value);
    if (numValue > 10000000) {
      setAmount('10000000');
    } else {
      setAmount(value);
    }
  }

  const balance = portfolioSummary?.cashBalance || 0;

  function handleAmountQuickAdd(addAmount: number | 'max') {
    if (addAmount === 'max') {
      // Set amount to the user's full available cash balance, capped at 10M
      const cappedBalance = Math.min(10000000, cashBalance);
      setAmount(cappedBalance > 0 ? cappedBalance.toFixed(6) : '0');
    } else {
      const current = parseFloat(amount.replace(/[^0-9.]/g, '')) || 0;
      const nextVal = Math.min(10000000, current + addAmount);
      setAmount(nextVal.toString());
    }
  }

  const isInsufficientBalance = activeTab === 'buy' && totalCost > balance && totalCost > 0;
  const isInsufficientHolding = activeTab === 'sell' && sharesNum > userHolding;

  const buttonText = useMemo(() => {
    if (isSubmitting || isUpgradePending)
      return isUpgradePending
        ? 'Upgrading...'
        : t('trade.processing', { defaultValue: 'Processing...' });
    if (!isAuthenticated) return t('trade.trade', { defaultValue: 'Trade' });
    if (!isReadyToTrade && !cashBalance) return t('trade.loading', { defaultValue: 'Loading...' });
    if (activeTab === 'buy' && requiresMigration) return 'Upgrade to pUSD';
    if (isInsufficientBalance) return t('trade.deposit', { defaultValue: 'Deposit' });
    if (isInsufficientHolding) return 'Insufficient Holding';

    // Check for no bids when selling market order
    if (
      activeTab === 'sell' &&
      orderType === 'market' &&
      (!orderBook?.bids || orderBook.bids.length === 0)
    ) {
      return t('trade.noBids', { defaultValue: 'No Bids' });
    }

    // Check for no asks when buying market order
    if (
      activeTab === 'buy' &&
      orderType === 'market' &&
      (!orderBook?.asks || orderBook.asks.length === 0)
    ) {
      return t('trade.noAsks', { defaultValue: 'No Asks' });
    }

    // Min size validations
    if (activeTab === 'buy') {
      if (orderType === 'market' && amountNum > 0 && amountNum < 1)
        return t('trade.minAmount', { defaultValue: 'Min $1' });
      if (orderType === 'limit' && sharesNum > 0 && sharesNum < 5)
        return t('trade.minLimitShares', { defaultValue: 'Min 5 Shares' });
    } else {
      if (orderType === 'market' && sharesNum > 0 && sharesNum < 1)
        return t('trade.minShares', { defaultValue: 'Min 1 Share' });
      if (orderType === 'limit' && sharesNum > 0 && sharesNum < 5)
        return t('trade.minLimitShares', { defaultValue: 'Min 5 Shares' });
    }

    return activeTab === 'buy'
      ? t('trade.buy', { defaultValue: 'Buy' })
      : t('trade.sell', { defaultValue: 'Sell' });
  }, [
    isSubmitting,
    isUpgradePending,
    isAuthenticated,
    isReadyToTrade,
    requiresMigration,
    isInsufficientBalance,
    isInsufficientHolding,
    activeTab,
    orderType,
    amountNum,
    sharesNum,
    cashBalance,
    orderBook,
    t,
  ]);

  const isInputInvalid =
    isAuthenticated &&
    isReadyToTrade &&
    ((activeTab === 'buy' && orderType === 'market' && amountNum < 1) ||
      (orderType === 'limit' && (limitPriceNum <= 0 || sharesNum < 5)) ||
      (activeTab === 'sell' && orderType === 'market' && sharesNum < 1) ||
      isInsufficientHolding ||
      (activeTab === 'sell' &&
        orderType === 'market' &&
        (!orderBook?.bids || orderBook.bids.length === 0)) ||
      (activeTab === 'buy' &&
        orderType === 'market' &&
        (!orderBook?.asks || orderBook.asks.length === 0)));

  const handlePrimaryAction = () => {
    if (!isAuthenticated) {
      onLogin();
      return;
    }
    if (!user?.isReferredUser) {
      dispatch(openModal('referral'));
      return;
    }
    if (!isReadyToTrade) {
      dispatch(setSetupRequested(true));
      return;
    }
    if (activeTab === 'buy' && requiresMigration) {
      handleUpgrade();
      return;
    }
    if (isInsufficientBalance) {
      dispatch(openModal('deposit'));
      return;
    }
    handleTrade();
  };

  async function handleUpgrade() {
    if (!user?.proxyWalletAddress || legacyUsdcBalance <= 0) return;
    try {
      // Wrap entire legacy balance
      const amountBigInt = BigInt(Math.floor(legacyUsdcBalance * 1e6));
      await wrap(amountBigInt);
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[TradingSidebar] Upgrade failed:', err);
      }
    }
  }

  async function handleTrade() {
    if (!isAuthenticated) {
      onLogin();
      return;
    }
    if (!user?.isReferredUser) {
      dispatch(openModal('referral'));
      return;
    }
    dispatch(setSetupRequested(true));

    // 1. Resolve basic trade parameters
    const side = activeTab.toUpperCase() as 'BUY' | 'SELL';
    const isMarket = orderType === 'market';

    // 2. Determine the primary 'amount' field for the SDK
    const sdkAmount = side === 'BUY' && isMarket ? amountNum : sharesNum;

    // 3. Validation
    if (sdkAmount <= 0) {
      return;
    }
    if (!selectedTokenId) {
      return;
    }

    let expiration: number | undefined = undefined;
    if (orderType === 'limit' && setExpiration) {
      expiration = calculateExpirationTimestamp(expirationValue);
    }

    try {
      const result = await submitOrder({
        tokenId: selectedTokenId,
        amount: sdkAmount,
        price: !isMarket ? limitPriceNum / 100 : undefined,
        side,
        userUSDCBalance: cashBalance,
        expiration,
        negRisk: (market as any)?.negRisk || false,
      });

      if (result?.success) {
        // Reset inputs on success
        setAmount('0');
        setShares('0');
        if (isMarket) {
          setLimitPrice('0');
        }
      }
    } catch (err: any) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error placing order:', err);
      }
      const msg = String(err?.message || '').toLowerCase();
      const isAuthError = msg.includes('unauthorized') || msg.includes('invalid api key');
      if (isAuthError) {
        toast.error(
          'Authentication mismatch. Please click "Reset Trading Account" below to update your keys for the new Safe wallet.'
        );
      }
    }
  }

  const isResolved = isMarketResolved(market);

  if (!mounted) {
    return (
      <Box
        sx={{ minHeight: 400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      />
    );
  }

  if (isResolved) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          bgcolor: 'background.paper',
          borderRadius: '16px',
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <ResolvedTradeView event={event} market={market} />
      </Paper>
    );
  }

  return (
    <>
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          width: '100%',
          maxWidth: '100%',
          boxSizing: 'border-box',
        }}
      >
        <TradeHeader event={event} market={market} title={title} outcome={selectedOutcome} />

        <TradeTabs
          activeTab={activeTab}
          orderType={orderType}
          onTabChange={setActiveTab}
          onOrderTypeChange={(e) => {
            const value = e.target.value as string;
            if (value === '__merge__' || value === '__split__') return;
            setOrderType(value as any);
          }}
          onBuyTabClick={handleBuyTabClick}
          onSellTabClick={handleSellTabClick}
          onMoreActionSelect={(action) =>
            dispatch(
              openPositionActionModal({
                action,
                conditionId: (market as any)?.conditionId || '',
                tokenIds: tokenIds.slice(0, 2),
                eventId: event?.id,
              })
            )
          }
        />

        <SideSelector
          selectedSide={selectedSide}
          outcomeNameYes={yesLabelFinal}
          outcomeNameNo={noLabelFinal}
          yesPrice={yesPrice}
          noPrice={noPrice}
          yesBtnColor={yesBtnColorFinal}
          noBtnColor={noBtnColorFinal}
          onYesClick={handleYesClick}
          onNoClick={handleNoClick}
          isFlipped={isFlipped}
        />

        {orderType === 'limit' && (
          <LimitPriceInput
            limitPrice={limitPrice}
            onDecrease={handleLimitPriceDecrease}
            onIncrease={handleLimitPriceIncrease}
            onChange={handleLimitPriceChange}
          />
        )}

        {activeTab === 'buy' && orderType === 'market' && (
          <AmountInput
            amount={amount}
            balance={cashBalance}
            isAuthenticated={isAuthenticated}
            onChange={handleAmountChange}
            onQuickAdd={handleAmountQuickAdd}
          />
        )}

        {(orderType === 'limit' || activeTab === 'sell') && (
          <SharesInput
            shares={shares}
            activeTab={activeTab}
            isAuthenticated={isAuthenticated}
            userHolding={userHolding}
            sharesNum={sharesNum}
            isLoadingHolding={isPositionsFetching}
            onSharesChange={handleSharesChange}
            onQuickAmount={handleShareQuickAmount}
            onSetShares={setShares}
          />
        )}

        {orderType === 'limit' && (
          <ExpirationSelector
            setExpiration={setExpiration}
            onExpirationToggle={setSetExpiration}
            expirationValue={expirationValue}
            onExpirationValueChange={setExpirationValue}
          />
        )}

        {/* {orderType === 'market' && (averageFillPrice || effectivePrice) > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 1.25,
              mb: 1.25,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.default',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
                Avg. Price
              </Typography>
              <Typography sx={{ fontSize: '0.875rem', fontWeight: 700, color: 'text.primary' }}>
                {(((averageFillPrice || effectivePrice) as number) * 100).toFixed(2)}c
              </Typography>
            </Box>
          </Paper>
        )} */}

        <TradeSummary
          activeTab={activeTab}
          orderType={orderType}
          totalCost={totalCost}
          potReturn={potReturn}
          potProfit={potProfit}
        />

        {orderError && (
          <Box sx={{ mb: 2, textAlign: 'center' }}>
            {orderError.message.toLowerCase().includes('unauthorized') ||
            orderError.message.toLowerCase().includes('invalid api key') ? (
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={async () => {
                  await resetCredentials();
                  // Re-trigger the setup flow automatically
                  dispatch(setSetupRequested(true));
                }}
                sx={{ fontSize: '0.65rem', py: 0.5 }}
              >
                Reset Trading Account
              </Button>
            ) : null}
          </Box>
        )}
        <TradeActionButton
          onClick={handlePrimaryAction}
          disabled={isSubmitting || (isReadyToTrade && isInputInvalid)}
          buttonText={buttonText}
        />
      </Paper>
    </>
  );
}

export default TradingSidebar;
